import { Component, ElementRef, OnInit, ViewChild, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { AgentGateway } from '../../../gateways/agent.gateway';
import { Agent, runtimeLabel } from '../../../models/agent.model';

type ListState = 'loading' | 'ready' | 'empty' | 'error';

@Component({
    selector: 'app-agents',
    standalone: true,
    imports: [CommonModule, RouterLink],
    template: `
        <div class="page-content">
            <div class="page-header">
                <div>
                    <h1>Agents</h1>
                    <p>Create and manage the agents in this project.</p>
                </div>
                <a class="btn btn-primary" [routerLink]="['../agents/new']">New agent</a>
            </div>

            @if (state() === 'loading') {
                <div class="state" role="status" aria-live="polite">Loading agents…</div>
            }

            @if (state() === 'error') {
                <div class="state state-error" role="alert">
                    <p>{{ errorMessage() }}</p>
                    <button type="button" class="btn" (click)="load()">Retry</button>
                </div>
            }

            @if (state() === 'empty') {
                <div class="state" role="status">
                    <p>No agents yet.</p>
                    <a class="btn btn-primary" [routerLink]="['../agents/new']"
                        >Create your first agent</a
                    >
                </div>
            }

            @if (state() === 'ready') {
                <ul class="agent-list">
                    @for (agent of agents(); track agent.id) {
                        <li class="agent-row">
                            <div class="agent-info">
                                <a class="agent-name" [routerLink]="['../agents', agent.id]">{{
                                    agent.name
                                }}</a>
                                <p class="agent-description">{{ agent.description }}</p>
                                <div class="agent-meta">
                                    <span class="badge">{{
                                        runtimeLabelFor(agent.runtimeId)
                                    }}</span>
                                    <span class="badge badge-status">{{ agent.status }}</span>
                                </div>
                            </div>
                            <div class="agent-actions">
                                <a class="btn btn-sm" [routerLink]="['../agents', agent.id]"
                                    >View</a
                                >
                                <a class="btn btn-sm" [routerLink]="['../agents', agent.id, 'edit']"
                                    >Edit</a
                                >
                                <button
                                    type="button"
                                    class="btn btn-sm btn-danger"
                                    (click)="requestDelete(agent)"
                                >
                                    Delete
                                </button>
                            </div>
                        </li>
                    }
                </ul>
            }

            @if (rowError()) {
                <div class="state state-error" role="alert">{{ rowError() }}</div>
            }
        </div>

        @if (pendingDelete(); as target) {
            <div class="dialog-backdrop" (click)="cancelDelete()"></div>
            <div
                #dialog
                class="dialog"
                role="dialog"
                aria-modal="true"
                aria-labelledby="delete-title"
                (keydown)="onDialogKeydown($event)"
            >
                <h2 id="delete-title">Delete agent</h2>
                <p>Delete "{{ target.name }}"? This cannot be undone.</p>
                <div class="dialog-actions">
                    <button #cancelBtn type="button" class="btn" (click)="cancelDelete()">
                        Cancel
                    </button>
                    <button type="button" class="btn btn-danger" (click)="confirmDelete()">
                        Delete
                    </button>
                </div>
            </div>
        }
    `,
    styles: [
        `
            .page-content {
                padding: 2rem;
            }
            .page-header {
                display: flex;
                justify-content: space-between;
                align-items: flex-start;
                margin-bottom: 1.5rem;
                gap: 1rem;
            }
            h1 {
                font-size: 1.75rem;
                font-weight: 600;
                margin-bottom: 0.5rem;
                color: #1a1a1a;
            }
            p {
                color: #666;
                margin: 0;
            }
            .state {
                padding: 2rem;
                background: white;
                border: 1px solid #e0e0e0;
                border-radius: 8px;
                text-align: center;
                color: #666;
            }
            .state-error {
                border-color: #f5c2c2;
                background: #fdecea;
                color: #a12622;
            }
            .agent-list {
                list-style: none;
                margin: 0;
                padding: 0;
                display: flex;
                flex-direction: column;
                gap: 0.75rem;
            }
            .agent-row {
                display: flex;
                justify-content: space-between;
                align-items: center;
                gap: 1rem;
                padding: 1rem 1.25rem;
                background: white;
                border: 1px solid #e0e0e0;
                border-radius: 8px;
            }
            .agent-name {
                font-weight: 600;
                color: #1a1a1a;
                text-decoration: none;
                font-size: 1.05rem;
            }
            .agent-name:hover {
                color: #2196f3;
            }
            .agent-description {
                margin: 0.25rem 0 0.5rem;
            }
            .agent-meta {
                display: flex;
                gap: 0.5rem;
            }
            .badge {
                display: inline-block;
                font-size: 0.75rem;
                padding: 0.15rem 0.5rem;
                border-radius: 999px;
                background: #eef2f7;
                color: #445;
            }
            .badge-status {
                background: #e3f2fd;
                color: #1565c0;
            }
            .agent-actions {
                display: flex;
                gap: 0.5rem;
                flex-shrink: 0;
            }
            .btn {
                display: inline-block;
                padding: 0.5rem 0.9rem;
                border-radius: 6px;
                border: 1px solid #d0d0d0;
                background: white;
                color: #1a1a1a;
                text-decoration: none;
                cursor: pointer;
                font-size: 0.9rem;
            }
            .btn:hover {
                background: #f5f5f5;
            }
            .btn-primary {
                background: #2196f3;
                border-color: #2196f3;
                color: white;
            }
            .btn-primary:hover {
                background: #1976d2;
            }
            .btn-danger {
                border-color: #e0b4b4;
                color: #a12622;
            }
            .btn-danger:hover {
                background: #fdecea;
            }
            .btn-sm {
                padding: 0.35rem 0.7rem;
                font-size: 0.85rem;
            }
            .dialog-backdrop {
                position: fixed;
                inset: 0;
                background: rgba(0, 0, 0, 0.35);
            }
            .dialog {
                position: fixed;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
                background: white;
                border-radius: 8px;
                padding: 1.5rem;
                width: min(90vw, 420px);
                box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
            }
            .dialog h2 {
                margin: 0 0 0.75rem;
                font-size: 1.25rem;
            }
            .dialog-actions {
                display: flex;
                justify-content: flex-end;
                gap: 0.5rem;
                margin-top: 1.5rem;
            }
        `,
    ],
})
export class Agents implements OnInit {
    private readonly route = inject(ActivatedRoute);
    private readonly gateway = inject(AgentGateway);

    @ViewChild('cancelBtn') private cancelBtn?: ElementRef<HTMLButtonElement>;
    @ViewChild('dialog') private dialog?: ElementRef<HTMLElement>;

    readonly state = signal<ListState>('loading');
    readonly agents = signal<Agent[]>([]);
    readonly errorMessage = signal('');
    readonly rowError = signal('');
    readonly pendingDelete = signal<Agent | null>(null);

    private projectId = '';
    private triggerElement: HTMLElement | null = null;

    ngOnInit(): void {
        this.projectId = this.route.snapshot.paramMap.get('projectId') ?? '';
        this.load();
    }

    load(): void {
        this.state.set('loading');
        this.errorMessage.set('');
        this.rowError.set('');
        this.gateway.listAgents(this.projectId).subscribe({
            next: (agents) => {
                this.agents.set(agents);
                this.state.set(agents.length ? 'ready' : 'empty');
            },
            error: (err: Error) => {
                this.errorMessage.set(err.message);
                this.state.set('error');
            },
        });
    }

    runtimeLabelFor(id: string): string {
        return runtimeLabel(id);
    }

    requestDelete(agent: Agent): void {
        this.rowError.set('');
        this.triggerElement = document.activeElement as HTMLElement | null;
        this.pendingDelete.set(agent);
        // Move focus into the dialog once it renders.
        queueMicrotask(() => this.cancelBtn?.nativeElement.focus());
    }

    cancelDelete(): void {
        this.pendingDelete.set(null);
        this.restoreFocus();
    }

    confirmDelete(): void {
        const target = this.pendingDelete();
        if (!target) {
            return;
        }
        // Optimistic removal.
        const previous = this.agents();
        this.agents.set(previous.filter((a) => a.id !== target.id));
        this.pendingDelete.set(null);
        this.restoreFocus();
        if (!this.agents().length) {
            this.state.set('empty');
        }

        this.gateway.deleteAgent(this.projectId, target.id).subscribe({
            error: (err: Error) => {
                // Restore on failure.
                this.agents.set(previous);
                this.state.set('ready');
                this.rowError.set(err.message);
            },
        });
    }

    onDialogKeydown(event: KeyboardEvent): void {
        if (event.key === 'Escape') {
            event.preventDefault();
            this.cancelDelete();
            return;
        }
        if (event.key === 'Tab') {
            this.trapFocus(event);
        }
    }

    private trapFocus(event: KeyboardEvent): void {
        const host = this.dialog?.nativeElement;
        if (!host) {
            return;
        }
        const focusable = host.querySelectorAll<HTMLElement>(
            'button, [href], input, select, textarea',
        );
        if (!focusable.length) {
            return;
        }
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        const active = document.activeElement;
        if (event.shiftKey && active === first) {
            event.preventDefault();
            last.focus();
        } else if (!event.shiftKey && active === last) {
            event.preventDefault();
            first.focus();
        }
    }

    private restoreFocus(): void {
        this.triggerElement?.focus();
        this.triggerElement = null;
    }
}
